import { Icon } from '@iconify/react';
import { styled } from '@linaria/react';
import { Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import * as Sentry from '@sentry/browser';
import { useState } from 'react';
import { useCreateFloraFeedbackMutation } from '../../../../api/flora-feedback-client/flora-feedback-client.hooks';
import {
  type FloraFeedbackPayload,
  FloraFeedbackReason,
  FloraFeedbackSentiment,
} from '../../../../api/flora-feedback-client/flora-feedback-client.type';
import { Text } from '../../../../ui-library/typography/typography';
import { toast } from '../../../notifications/notifications';
import { FloraFeedbackModal } from './flora-feedback-modal';

interface FloraFeedbackProps {
  chatHistoryId: string;
}

/**
 * Thumbs up/down feedback control for the most-recent Flora response.
 *
 * Renders two icon buttons that open a rating modal; on a successful submit (or a
 * 409 meaning the response was already rated) the buttons are replaced with a
 * non-interactive "Feedback sent" state — there is no editing after submit. The
 * built payload sends reasons only for negative sentiment and omits an empty
 * comment. Gating to the latest live-session turn is the caller's responsibility.
 *
 * @param {FloraFeedbackProps} props - Component props.
 * @param {string} props.chatHistoryId - The current chat/thread id; sent as `chat_history_id`.
 * @returns {JSX.Element} The feedback buttons (or the "Feedback sent" state once submitted).
 */
export const FloraFeedback = ({ chatHistoryId }: FloraFeedbackProps) => {
  const [submitted, setSubmitted] = useState(false);
  const [opened, { open, close }] = useDisclosure(false);
  const [sentiment, setSentiment] = useState<FloraFeedbackSentiment>(FloraFeedbackSentiment.Positive);
  const mutation = useCreateFloraFeedbackMutation();

  const openWith = (next: FloraFeedbackSentiment) => {
    setSentiment(next);
    open();
  };

  const handleSubmit = ({
    sentiment,
    reasons,
    comment,
  }: {
    sentiment: FloraFeedbackSentiment;
    reasons: FloraFeedbackReason[];
    comment: string;
  }) => {
    const trimmedComment = comment.trim();
    const payload: FloraFeedbackPayload = {
      chat_history_id: chatHistoryId,
      sentiment,
      ...(sentiment === FloraFeedbackSentiment.Negative ? { reasons } : {}),
      ...(trimmedComment ? { comment: trimmedComment } : {}),
    };

    mutation.mutate(payload, {
      onSuccess: () => {
        setSubmitted(true);
        close();
        toast.success('Thanks for your feedback!');
      },
      onError: (error) => {
        // 409 = feedback already recorded for this response. Treat it as "already rated".
        if (error?.status === 409) {
          setSubmitted(true);
          close();
          toast.success('You already rated this response.');
          return;
        }
        // Non-409 failures are invisible to the user beyond a toast — report them so we
        // have a signal if the endpoint starts failing. The backend body rides along in extra.
        Sentry.captureException(new Error('Flora feedback submission failed'), {
          level: 'warning',
          extra: { chatHistoryId, status: error?.status, data: error?.data },
        });
        toast.error('Could not submit feedback. Please try again.');
      },
    });
  };

  if (submitted) {
    return (
      <SentRow data-testid="flora-feedback-sent">
        <Icon icon="material-symbols:check-circle-outline" width={16} height={16} />
        <Text size="small" color="var(--ink-light)">
          Feedback sent
        </Text>
      </SentRow>
    );
  }

  return (
    <>
      <ButtonsRow data-testid="flora-feedback-buttons">
        <Tooltip label="Good response" position="top" withinPortal={false}>
          <FeedbackButton
            type="button"
            aria-label="Good response"
            onClick={() => openWith(FloraFeedbackSentiment.Positive)}
          >
            <Icon icon="material-symbols:thumb-up-outline" width={16} height={16} />
          </FeedbackButton>
        </Tooltip>
        <Tooltip label="Bad response" position="top" withinPortal={false}>
          <FeedbackButton
            type="button"
            aria-label="Bad response"
            onClick={() => openWith(FloraFeedbackSentiment.Negative)}
          >
            <Icon icon="material-symbols:thumb-down-outline" width={16} height={16} />
          </FeedbackButton>
        </Tooltip>
      </ButtonsRow>
      <FloraFeedbackModal
        opened={opened}
        sentiment={sentiment}
        isSubmitting={mutation.isPending}
        onSubmit={handleSubmit}
        onClose={close}
      />
    </>
  );
};

const ButtonsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const FeedbackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: var(--ink-lighter);
  border-radius: 4px;
  transition: color 0.15s, background 0.15s;

  &:hover {
    color: var(--secondary-base);
    background: var(--flora-hover-subtle, var(--sky-lighter));
  }
`;

const SentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--ink-light);
`;
